// Static marketing copy shared by several pages. Database-driven content
// (models, testimonials, questions) lives in the CMS instead.

import type { LucideIcon } from 'lucide-react'

import {
    BatteryCharging,
    Compass,
    Flame,
    Hammer,
    Handshake,
    KeyRound,
    Layers,
    MessagesSquare,
    Palette,
    PencilRuler,
    Recycle,
    ShieldCheck,
    Snowflake,
    Sofa,
    Sprout,
    ThermometerSun,
    Trees,
    Truck,
    Wrench
} from 'lucide-react'

export type IconItem = {
    icon: LucideIcon
    title: string
    text: string
}

export type ProcessStep = IconItem & {
    number: string
    meta: string
}

export const HERO_POINTS: { icon: LucideIcon, label: string }[] = [
    { icon: Truck, label: "Road-legal and ready to tow" },
    { icon: Snowflake, label: "Insulated for all four seasons" },
    { icon: ShieldCheck, label: "Fixed price, fixed schedule" }
]

export const BENEFITS: IconItem[] = [
    {
        icon: Hammer,
        title: "Made by hand, never outsourced",
        text: "Every frame is cut, assembled and finished in our own workshop by the same small team."
    },
    {
        icon: Trees,
        title: "Natural, healthy materials",
        text: "Solid timber, wood-fibre insulation and larch cladding that weathers to a soft silver grey."
    },
    {
        icon: Truck,
        title: "Road-legal and ready to move",
        text: "Built on certified trailers within 2.55 m wide and 3.5 tonnes, so your home can travel with you."
    },
    {
        icon: ThermometerSun,
        title: "Comfortable in every season",
        text: "Airtight construction and thick insulation keep you warm in winter and cool in summer."
    }
]

export const PROCESS_STEPS: ProcessStep[] = [
    {
        number: "01",
        icon: MessagesSquare,
        title: "Talk it through",
        text: "Tell us how you want to live, where the house will go and what you want to spend. We recommend a model or a custom route.",
        meta: "Free consultation"
    },
    {
        number: "02",
        icon: PencilRuler,
        title: "Design and fixed quote",
        text: "We draw your layout, choose materials with you and send a fixed price together with a build schedule.",
        meta: "2 to 4 weeks"
    },
    {
        number: "03",
        icon: Hammer,
        title: "Built in our workshop",
        text: "Your home is built by the same small team from chassis to final finish, with photo updates every week.",
        meta: "10 to 16 weeks"
    },
    {
        number: "04",
        icon: KeyRound,
        title: "Delivery and handover",
        text: "We bring the house to your land, level it, connect it and walk you through every detail before handing over the keys.",
        meta: "Delivery day"
    }
]

export const PROCESS_DETAILS: (ProcessStep & { points: string[] })[] = [
    {
        ...PROCESS_STEPS[0],
        points: [
            "A relaxed call or a visit to the workshop",
            "Your budget, your land and how you plan to live",
            "An honest view of which model fits, or whether custom makes more sense"
        ]
    },
    {
        ...PROCESS_STEPS[1],
        points: [
            "Floor plan and elevations drawn around your needs",
            "Material, finish and option choices made together",
            "A fixed price and a booked build slot, with no surprises later"
        ]
    },
    {
        ...PROCESS_STEPS[2],
        points: [
            "Steel chassis, timber frame and insulation built in-house",
            "Weekly photos and an open invitation to visit",
            "Quality checks at every stage before the walls are closed"
        ]
    },
    {
        ...PROCESS_STEPS[3],
        points: [
            "Transport arranged and insured by us",
            "Levelling and connection to water, power and drainage",
            "A full walk-through and a handbook for your home"
        ]
    },
    {
        number: "05",
        icon: Wrench,
        title: "Aftercare",
        text: "We stay in touch long after delivery. If something needs adjusting, or you want to change the house later, we are a call away.",
        meta: "For the life of your home",
        points: [
            "Help with maintenance and seasonal checks",
            "Upgrades and changes when your life changes",
            "A direct line to the people who built your home"
        ]
    }
]

export const MATERIALS: { title: string, text: string }[] = [
    {
        title: "Chassis",
        text: "A galvanised steel trailer, certified for the road and sized precisely for each model."
    },
    {
        title: "Frame",
        text: "Solid kiln-dried timber, braced to handle travel as well as everyday life."
    },
    {
        title: "Insulation",
        text: "Breathable wood fibre in the walls, floor and roof, for steady warmth and a healthy indoor climate."
    },
    {
        title: "Cladding and roof",
        text: "Larch or Douglas fir cladding and a standing-seam metal roof that sheds rain and snow."
    },
    {
        title: "Windows",
        text: "High-performance glazing in timber frames, placed to bring light deep into every room."
    },
    {
        title: "Interior",
        text: "Birch plywood or solid timber linings, with fitted furniture built in our workshop."
    }
]

export const OPTIONS: IconItem[] = [
    {
        icon: BatteryCharging,
        title: "Off-grid pack",
        text: "Solar panels, battery storage and a smart controller to live without a mains connection."
    },
    {
        icon: Flame,
        title: "Wood stove",
        text: "A compact stove with a certified flue for real fire on cold evenings."
    },
    {
        icon: Recycle,
        title: "Composting toilet",
        text: "A dry toilet that needs no water or drainage, ideal for remote plots."
    },
    {
        icon: Snowflake,
        title: "Mountain pack",
        text: "Extra insulation, frost-proof plumbing and a reinforced roof for harsh winters."
    },
    {
        icon: Palette,
        title: "Cladding and colours",
        text: "Natural larch, charred timber or painted boards in the colour of your choice."
    },
    {
        icon: Sofa,
        title: "Fitted furniture",
        text: "Sofas, beds, desks and storage designed for your layout and built in our workshop."
    }
]

export const VALUES: IconItem[] = [
    {
        icon: Hammer,
        title: "Craft over volume",
        text: "We build a limited number of homes each year so that every one gets our full attention."
    },
    {
        icon: Handshake,
        title: "Honest from the start",
        text: "Clear prices, realistic schedules and straight answers, even when the answer is not what you hoped."
    },
    {
        icon: Sprout,
        title: "A lighter footprint",
        text: "Natural materials, small spaces and efficient systems mean less energy and less waste."
    },
    {
        icon: Compass,
        title: "Homes for real life",
        text: "We design around how you actually live, not around what looks good in a brochure."
    }
]

export const INCLUDED: string[] = [
    "Certified road-legal trailer",
    "Solid timber frame and wood-fibre insulation",
    "Cladding, roofing and high-performance windows",
    "Fitted kitchen with appliances",
    "Shower room with toilet",
    "Full electrical installation and lighting",
    "Interior linings, flooring and stairs",
    "Handover walk-through and home handbook"
]

export const CUSTOM_POINTS: string[] = [
    "Layouts drawn from scratch around your needs",
    "Your choice of cladding, timber and finishes",
    "Off-grid, accessible or mountain-ready specifications"
]

export const QUOTE_STEPS: { icon: LucideIcon, title: string, text: string }[] = [
    {
        icon: MessagesSquare,
        title: "We read your request",
        text: "A member of the team looks at your plans personally, never an automated system."
    },
    {
        icon: Layers,
        title: "We come back with ideas",
        text: "You get suggestions, a realistic price range and answers to your questions."
    },
    {
        icon: KeyRound,
        title: "You decide, no pressure",
        text: "If it feels right, we book a design session. If not, no hard feelings."
    }
]
